/**
 * Cloud Function client for calling the loadTimeseries function.
 * Uses Firebase compat SDK to share auth state with the main app.
 */

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import { CandleDataPoint, LoadTimeseriesResponse, LoadTimeseriesRow } from './types';
import { waitForAuth } from '../../../../data/authUtils';

/**
 * Parameters for the loadTimeseries Cloud Function.
 */
interface LoadTimeseriesParams {
    series: string;
    from: number;
    to: number;
}

// Nanosecond timestamp for year 2100 - covers all practical data
const FAR_FUTURE_NS = 4102444800000000000;

/**
 * Parse timestamp from Cloud Function response.
 * Handles both ISO date strings (from PostgreSQL) and nanosecond numeric strings.
 */
function parseTimestamp(timestampStr: string): Date {
    // Check if it's an ISO date string (contains letters like T, Z, or -)
    if (/[A-Za-z-]/.test(timestampStr)) {
        return new Date(timestampStr);
    }
    // Otherwise treat as nanosecond numeric string
    const nanos = BigInt(timestampStr);
    const millis = nanos / BigInt(1_000_000);
    return new Date(Number(millis));
}

/**
 * Transform a Cloud Function row to a CandleDataPoint.
 */
function transformRow(row: LoadTimeseriesRow): CandleDataPoint {
    return {
        timestamp: parseTimestamp(row.timestamp),
        open: row.data.open,
        high: row.data.high,
        low: row.data.low,
        close: row.data.close,
        baseVolume: row.data.base_volume,
        quoteVolume: row.data.quote_volume,
        tradeCount: row.data.trade_count,
        vwap: row.data.vwap,
        openTimestamp: parseTimestamp(row.data.open_timestamp),
        closeTimestamp: parseTimestamp(row.data.close_timestamp),
        isFilled: false,
    };
}

/**
 * Load timeseries candle data from the Cloud Function.
 *
 * @param series - The full series ID (e.g., "bogg.polymarket:123.yes.polymarket_candle.60000000000")
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Array of CandleDataPoint sorted by timestamp ascending
 */
export async function loadTimeseriesData(
    series: string,
    signal?: AbortSignal
): Promise<CandleDataPoint[]> {
    const functions = firebase.app().functions('europe-west6');
    const loadTimeseriesCallable = functions.httpsCallable('loadTimeseries');

    const params: LoadTimeseriesParams = {
        series,
        from: 0,
        to: FAR_FUTURE_NS,
    };

    await waitForAuth();

    // Check if aborted before making the expensive network call
    if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
    }

    try {
        const result = await loadTimeseriesCallable(params);

        // Check if aborted after the response returns
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }

        const response = result.data as LoadTimeseriesResponse;

        console.log(`[loadTimeseriesData] Received ${response.rows.length} rows, truncated: ${response.truncated}`);

        if (response.truncated) {
            console.warn('[loadTimeseriesData] Response was truncated - consider using a smaller date range');
        }

        // Transform and sort by timestamp
        const candles = response.rows.map(transformRow);
        candles.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        return candles;
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            console.log(`[loadTimeseriesData] Request aborted for series: ${series}`);
            throw error;
        }
        console.error('[loadTimeseriesData] Cloud Function error:', error);
        throw error;
    }
}

/**
 * Load all available candle data for a series.
 *
 * @param series - The full series ID
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Array of CandleDataPoint
 */
export async function loadFullSeriesData(
    series: string,
    signal?: AbortSignal
): Promise<CandleDataPoint[]> {
    return loadTimeseriesData(series, signal);
}
